import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { CircleOff } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useSexualHealth } from "../SexualHealthContext";
import type { PapSmearProcedureDetail } from "../SexualHealthContext";
import { buildSexualHealthNavigationStack } from "../buildSexualHealthNavigationStack";
import { PAP_SMEAR_PROCEDURE_OPTIONS } from "../options";
import type { PapSmearProcedureType } from "../options";
import type { SexualHealthStackParamList } from "../SexualHealthNavigator";

export function PapSmearProceduresScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useSexualHealth();
  const navigation =
    useNavigation<NavigationProp<SexualHealthStackParamList>>();

  const selectedProcedures = useMemo(
    () => new Set(state.papSmearProcedures.map((p) => p.procedure)),
    [state.papSmearProcedures],
  );

  const isNone = state.hadPapSmearProcedures === false;

  const toggleProcedure = (procedureType: PapSmearProcedureType) => {
    if (selectedProcedures.has(procedureType)) {
      // Remove
      const toRemove = state.papSmearProcedures.find(
        (p) => p.procedure === procedureType,
      );
      const idsToDelete = toRemove?.id
        ? [...state.procedureIdsToDelete, toRemove.id]
        : state.procedureIdsToDelete;
      const remaining = state.papSmearProcedures.filter(
        (p) => p.procedure !== procedureType,
      );
      updateState({
        hadPapSmearProcedures: remaining.length > 0 ? true : null,
        papSmearProcedures: remaining,
        procedureIdsToDelete: idsToDelete,
      });
    } else {
      // Add
      const newProcedure: PapSmearProcedureDetail = {
        procedure: procedureType,
      };
      updateState({
        hadPapSmearProcedures: true,
        papSmearProcedures: [...state.papSmearProcedures, newProcedure],
      });
    }
  };

  const handleNone = () => {
    const idsToDelete = state.papSmearProcedures
      .filter((p) => p.id)
      .map((p) => p.id!);
    updateState({
      hadPapSmearProcedures: false,
      papSmearProcedures: [],
      procedureIdsToDelete: [...state.procedureIdsToDelete, ...idsToDelete],
    });
  };

  const handleSave = async () => {
    await save();
    const stack = buildSexualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("PapSmearProcedures");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  const isValid =
    isNone ||
    (state.hadPapSmearProcedures === true &&
      state.papSmearProcedures.length > 0);

  return (
    <QuestionLayout
      title={t("med_history.gynae.sexual_health.pap_smear_procedures_title")}
      subtitle={t(
        "med_history.gynae.sexual_health.pap_smear_procedures_subtitle",
      )}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={!isValid}
      saveLabel={t("med_history.gynae.sexual_health.continue")}
    >
      <View style={styles.container}>
        <View style={styles.grid}>
          {PAP_SMEAR_PROCEDURE_OPTIONS.map((option) => (
            <View key={option.id} style={styles.cell}>
              <SquareOptionButton
                icon={option.icon}
                label={t(option.labelKey)}
                isSelected={!isNone && selectedProcedures.has(option.id)}
                onPress={() => {
                  if (isNone) {
                    updateState({
                      hadPapSmearProcedures: true,
                      papSmearProcedures: [{ procedure: option.id }],
                    });
                  } else {
                    toggleProcedure(option.id);
                  }
                }}
                labelSize="sm"
              />
            </View>
          ))}
          <View style={styles.cell}>
            <SquareOptionButton
              icon={CircleOff}
              label={t("med_history.gynae.sexual_health.none")}
              isSelected={isNone}
              onPress={handleNone}
              labelSize="sm"
            />
          </View>
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    width: "100%",
  },
  cell: {
    width: "47%",
  },
});
